import { ExclamationIcon } from '@heroicons/react/outline';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Button from '../../../components/Button';
import Dialog from '../../../components/Dialog';

const ProjectWizardPublishControl = ({
  hasDraft,
  onPreparePublish,
  onClearDraft,
  onCommitDraft,
}) => {
  const [isDraftDialogVisible, setIsDraftDialogVisible] = useState(false);
  const [isPreparingSave, setIsPreparingSave] = useState(false);
  const [canCommitDraft, setCanCommitDraft] = useState(false);

  const handleOpenSaveDialog = () => {
    setIsPreparingSave(true);
    onPreparePublish()
      .then(({ hasPendingChanges }) => {
        setCanCommitDraft(hasPendingChanges);
        setIsDraftDialogVisible(true);
      })
      .finally(() => setIsPreparingSave(false));
  };

  const handleCloseSaveDialog = () => {
    setIsDraftDialogVisible(false);
    setCanCommitDraft(false);
  };

  return (
    <>
      <Button
        data-testid="button-PublishChanges"
        type={hasDraft ? 'primary' : 'secondary'}
        loading={isPreparingSave}
        onClick={handleOpenSaveDialog}
      >
        Publish changes
      </Button>
      <Dialog
        title="Publish changes?"
        visible={isDraftDialogVisible}
        onCancel={handleCloseSaveDialog}
        footer={
          <div className="cluster-2">
            <Button
              data-testid="button-draft-clear"
              type="danger"
              disabled={!hasDraft && !canCommitDraft}
              onClick={() => {
                handleCloseSaveDialog();
                onClearDraft();
              }}
            >
              Clear
            </Button>
            <Button
              data-testid="button-draft-cancel"
              onClick={handleCloseSaveDialog}
            >
              Cancel
            </Button>
            <Button
              data-testid="button-draft-save"
              type="primary"
              disabled={!hasDraft && !canCommitDraft}
              onClick={() => {
                handleCloseSaveDialog();
                onCommitDraft();
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        {hasDraft || canCommitDraft ? (
          <div className="stack-4">
            <p className="text-sm text-neutral-300 dark:text-neutral-300 m-0">
              This will make the latest project configuration visible to users.
            </p>
            <div className="cluster-3 items-start rounded-md border border-warning bg-warning-muted dark:bg-neutral-800 p-4 text-on-warning dark:text-warning">
              <ExclamationIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="stack-1">
                <p className="font-medium m-0">
                  Publishing updates the live project.
                </p>
                <p className="text-sm m-0">
                  Patients and providers may see these changes after publishing.
                  Review the project settings before confirming.
                </p>
              </div>
            </div>
            <div className="cluster-3 items-start rounded-md border border-warning bg-warning-muted dark:bg-neutral-800 p-4 text-on-warning dark:text-warning">
              <ExclamationIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="stack-1">
                <p className="font-medium m-0">
                  Participations and activities will be scanned after
                  publishing.
                </p>
                <p className="text-sm m-0">
                  After publish, all related recipient participation and
                  activity will be scanned and checked to ensure alignment with
                  the latest project configuration.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="m-0">No pending changes. The project is up to date.</p>
        )}
      </Dialog>
    </>
  );
};

ProjectWizardPublishControl.propTypes = {
  hasDraft: PropTypes.bool,
  onPreparePublish: PropTypes.func.isRequired,
  onClearDraft: PropTypes.func.isRequired,
  onCommitDraft: PropTypes.func.isRequired,
};

ProjectWizardPublishControl.defaultProps = {
  hasDraft: false,
};

export default ProjectWizardPublishControl;
